import bindAll from 'lodash.bindall';
import PropTypes from 'prop-types';
import React from 'react';
import {FormattedMessage, defineMessages, injectIntl, intlShape} from 'react-intl';
import Modal from '../../containers/modal.jsx';
import Box from '../box/box.jsx';
import {changePassword, updateProfile} from '../../lib/auth-api.js';
import {describeAuthError} from '../../lib/auth-error-messages.js';
import styles from './account-settings.css';

/*
 * "账号设置": change the display name and the password.
 *
 * The account name itself is fixed, because it identifies the account and is
 * what other people would use to find it. Changing the password requires the
 * current one, so a session left open on a shared computer cannot be used to
 * take the account over.
 */
const messages = defineMessages({
    title: {
        id: 'gui.accountSettings.title',
        defaultMessage: '账号设置',
        description: 'Title of the account settings dialog'
    },
    accountLabel: {
        id: 'gui.accountSettings.account',
        defaultMessage: '账号',
        description: 'Label for the read-only account name'
    },
    accountHint: {
        id: 'gui.accountSettings.accountHint',
        defaultMessage: '账号是登录用的标识，不能修改。',
        description: 'Explains why the account name cannot be edited'
    },
    displayNameLabel: {
        id: 'gui.accountSettings.displayName',
        defaultMessage: '显示名称',
        description: 'Label for the display name field'
    },
    saveProfile: {
        id: 'gui.accountSettings.saveProfile',
        defaultMessage: '保存显示名称',
        description: 'Button that saves the display name'
    },
    profileSaved: {
        id: 'gui.accountSettings.profileSaved',
        defaultMessage: '显示名称已更新。',
        description: 'Confirmation after saving the display name'
    },
    passwordHeading: {
        id: 'gui.accountSettings.passwordHeading',
        defaultMessage: '修改密码',
        description: 'Heading above the password change form'
    },
    currentPassword: {
        id: 'gui.accountSettings.currentPassword',
        defaultMessage: '当前密码',
        description: 'Label for the current password field'
    },
    newPassword: {
        id: 'gui.accountSettings.newPassword',
        defaultMessage: '新密码',
        description: 'Label for the new password field'
    },
    confirmPassword: {
        id: 'gui.accountSettings.confirmPassword',
        defaultMessage: '确认新密码',
        description: 'Label for the repeated new password field'
    },
    savePassword: {
        id: 'gui.accountSettings.savePassword',
        defaultMessage: '修改密码',
        description: 'Button that changes the password'
    },
    passwordSaved: {
        id: 'gui.accountSettings.passwordSaved',
        defaultMessage: '密码已修改，请用新密码登录。',
        description: 'Confirmation after changing the password'
    },
    passwordHint: {
        id: 'gui.accountSettings.passwordHint',
        defaultMessage: '新密码需为 {min}–{max} 位。',
        description: 'Hint describing the password length rule'
    },
    passwordMismatch: {
        id: 'gui.accountSettings.passwordMismatch',
        defaultMessage: '两次输入的新密码不一致。',
        description: 'Error shown when the repeated password differs'
    },
    passwordTooShort: {
        id: 'gui.accountSettings.passwordTooShort',
        defaultMessage: '新密码长度需为 {min}–{max} 位。',
        description: 'Error shown when the new password is too short'
    },
    displayNameRequired: {
        id: 'gui.accountSettings.displayNameRequired',
        defaultMessage: '显示名称不能为空。',
        description: 'Error shown when the display name is emptied'
    }
});

const MIN_PASSWORD_LENGTH = 10;
const MAX_PASSWORD_LENGTH = 128;

class AccountSettings extends React.Component {
    constructor (props) {
        super(props);
        this.state = {
            displayName: (props.currentUser && props.currentUser.displayName) || '',
            currentPassword: '',
            newPassword: '',
            confirmPassword: '',
            isSavingProfile: false,
            isSavingPassword: false,
            profileNotice: null,
            profileError: null,
            passwordNotice: null,
            passwordError: null
        };
        bindAll(this, [
            'handleDisplayNameChange',
            'handleCurrentPasswordChange',
            'handleNewPasswordChange',
            'handleConfirmPasswordChange',
            'handleSaveProfile',
            'handleSavePassword'
        ]);
    }

    handleDisplayNameChange (event) {
        this.setState({displayName: event.target.value});
    }

    handleCurrentPasswordChange (event) {
        this.setState({currentPassword: event.target.value});
    }

    handleNewPasswordChange (event) {
        this.setState({newPassword: event.target.value});
    }

    handleConfirmPasswordChange (event) {
        this.setState({confirmPassword: event.target.value});
    }

    handleSaveProfile () {
        const displayName = this.state.displayName.trim();
        if (!displayName) {
            this.setState({
                profileError: this.props.intl.formatMessage(messages.displayNameRequired),
                profileNotice: null
            });
            return;
        }
        this.setState({isSavingProfile: true, profileError: null, profileNotice: null});
        updateProfile({displayName: displayName})
            .then(user => {
                this.setState({
                    isSavingProfile: false,
                    profileNotice: this.props.intl.formatMessage(messages.profileSaved)
                });
                this.props.onProfileUpdated(user);
            })
            .catch(error => {
                this.setState({
                    isSavingProfile: false,
                    profileError: this.props.intl.formatMessage(describeAuthError(error))
                });
            });
    }

    handleSavePassword () {
        const {currentPassword, newPassword, confirmPassword} = this.state;
        if (newPassword.length < MIN_PASSWORD_LENGTH || newPassword.length > MAX_PASSWORD_LENGTH) {
            this.setState({
                passwordError: this.props.intl.formatMessage(messages.passwordTooShort, {
                    min: MIN_PASSWORD_LENGTH,
                    max: MAX_PASSWORD_LENGTH
                }),
                passwordNotice: null
            });
            return;
        }
        if (newPassword !== confirmPassword) {
            this.setState({
                passwordError: this.props.intl.formatMessage(messages.passwordMismatch),
                passwordNotice: null
            });
            return;
        }
        this.setState({isSavingPassword: true, passwordError: null, passwordNotice: null});
        changePassword({currentPassword: currentPassword, newPassword: newPassword})
            .then(() => {
                this.setState({
                    isSavingPassword: false,
                    currentPassword: '',
                    newPassword: '',
                    confirmPassword: '',
                    passwordNotice: this.props.intl.formatMessage(messages.passwordSaved)
                });
            })
            .catch(error => {
                this.setState({
                    isSavingPassword: false,
                    passwordError: this.props.intl.formatMessage(describeAuthError(error))
                });
            });
    }

    render () {
        const {currentUser} = this.props;
        return (
            <Modal
                className={styles.modalContent}
                id="account-settings-modal"
                onRequestClose={this.props.onRequestClose}
            >
                <Box
                    className={styles.body}
                    id="account-settings-dialog"
                >
                    <h2 className={styles.title}>
                        <FormattedMessage {...messages.title} />
                    </h2>

                    <div className={styles.field}>
                        <label
                            className={styles.label}
                            htmlFor="account-settings-account"
                        >
                            <FormattedMessage {...messages.accountLabel} />
                        </label>
                        <input
                            className={styles.inputDisabled}
                            disabled
                            id="account-settings-account"
                            type="text"
                            value={(currentUser && currentUser.account) || ''}
                            readOnly
                        />
                        <span className={styles.hint}>
                            <FormattedMessage {...messages.accountHint} />
                        </span>
                    </div>

                    <div className={styles.field}>
                        <label
                            className={styles.label}
                            htmlFor="account-settings-display-name"
                        >
                            <FormattedMessage {...messages.displayNameLabel} />
                        </label>
                        <div className={styles.row}>
                            <input
                                className={styles.input}
                                disabled={this.state.isSavingProfile}
                                id="account-settings-display-name"
                                maxLength="64"
                                type="text"
                                value={this.state.displayName}
                                onChange={this.handleDisplayNameChange}
                            />
                            <button
                                className={styles.primaryButton}
                                disabled={this.state.isSavingProfile}
                                id="account-settings-save-profile"
                                type="button"
                                onClick={this.handleSaveProfile}
                            >
                                <FormattedMessage {...messages.saveProfile} />
                            </button>
                        </div>
                        {this.state.profileError ? (
                            <div
                                className={styles.error}
                                role="alert"
                            >
                                {this.state.profileError}
                            </div>
                        ) : null}
                        {this.state.profileNotice ? (
                            <div
                                className={styles.notice}
                                role="status"
                            >
                                {this.state.profileNotice}
                            </div>
                        ) : null}
                    </div>

                    <div className={styles.divider} />

                    <h3 className={styles.subtitle}>
                        <FormattedMessage {...messages.passwordHeading} />
                    </h3>

                    <div className={styles.field}>
                        <label
                            className={styles.label}
                            htmlFor="account-settings-current-password"
                        >
                            <FormattedMessage {...messages.currentPassword} />
                        </label>
                        <input
                            autoComplete="current-password"
                            className={styles.input}
                            disabled={this.state.isSavingPassword}
                            id="account-settings-current-password"
                            type="password"
                            value={this.state.currentPassword}
                            onChange={this.handleCurrentPasswordChange}
                        />
                    </div>

                    <div className={styles.field}>
                        <label
                            className={styles.label}
                            htmlFor="account-settings-new-password"
                        >
                            <FormattedMessage {...messages.newPassword} />
                        </label>
                        <input
                            autoComplete="new-password"
                            className={styles.input}
                            disabled={this.state.isSavingPassword}
                            id="account-settings-new-password"
                            type="password"
                            value={this.state.newPassword}
                            onChange={this.handleNewPasswordChange}
                        />
                        <span className={styles.hint}>
                            <FormattedMessage
                                {...messages.passwordHint}
                                values={{min: MIN_PASSWORD_LENGTH, max: MAX_PASSWORD_LENGTH}}
                            />
                        </span>
                    </div>

                    <div className={styles.field}>
                        <label
                            className={styles.label}
                            htmlFor="account-settings-confirm-password"
                        >
                            <FormattedMessage {...messages.confirmPassword} />
                        </label>
                        <input
                            autoComplete="new-password"
                            className={styles.input}
                            disabled={this.state.isSavingPassword}
                            id="account-settings-confirm-password"
                            type="password"
                            value={this.state.confirmPassword}
                            onChange={this.handleConfirmPasswordChange}
                        />
                    </div>

                    <button
                        className={styles.primaryButton}
                        disabled={this.state.isSavingPassword}
                        id="account-settings-save-password"
                        type="button"
                        onClick={this.handleSavePassword}
                    >
                        <FormattedMessage {...messages.savePassword} />
                    </button>

                    {this.state.passwordError ? (
                        <div
                            className={styles.error}
                            role="alert"
                        >
                            {this.state.passwordError}
                        </div>
                    ) : null}
                    {this.state.passwordNotice ? (
                        <div
                            className={styles.notice}
                            role="status"
                        >
                            {this.state.passwordNotice}
                        </div>
                    ) : null}
                </Box>
            </Modal>
        );
    }
}

AccountSettings.propTypes = {
    currentUser: PropTypes.shape({
        id: PropTypes.string,
        account: PropTypes.string,
        displayName: PropTypes.string
    }),
    intl: intlShape.isRequired,
    onProfileUpdated: PropTypes.func,
    onRequestClose: PropTypes.func.isRequired
};

export default injectIntl(AccountSettings);
export {AccountSettings};
